// src/components/SkippedQuestionsReview.tsx
import React from 'react';
import { SkippedQuestion } from '../types';
import './SkippedQuestionsReview.css';

interface SkippedQuestionsReviewProps {
  skippedQuestions: SkippedQuestion[];
  totalQuestionsAnswered: number;
  onContinue: () => void;
}

export const SkippedQuestionsReview: React.FC<SkippedQuestionsReviewProps> = ({
  skippedQuestions,
  totalQuestionsAnswered,
  onContinue,
}) => {
  // Group by section
  const bySection = skippedQuestions.reduce((acc, skip) => {
    if (!acc[skip.section]) {
      acc[skip.section] = [];
    }
    acc[skip.section].push(skip);
    return acc;
  }, {} as Record<string, SkippedQuestion[]>);

  const sectionNames: Record<string, string> = {
    profile: 'Organization Profile',
    privacy: 'Privacy Rule',
    admin: 'Administrative Safeguards',
    physical: 'Physical Safeguards',
    technical: 'Technical Safeguards',
    breach: 'Breach Preparedness',
    ba: 'Business Associates',
    ai: 'AI/ML Compliance',
    updates: '2026 Updates',
  };

  return (
    <div className="skipped-questions-review">
      <div className="skipped-header">
        <div className="skipped-icon">📋</div>
        <div className="skipped-content">
          <h2 className="skipped-title">Review Skipped Questions</h2>
          <p className="skipped-description">
            Based on your answers, {skippedQuestions.length} question{skippedQuestions.length > 1 ? 's were' : ' was'} skipped.
            You answered {totalQuestionsAnswered} questions.
          </p>
        </div>
      </div>

      <div className="skipped-stats">
        <div className="stat-box">
          <div className="stat-value">{totalQuestionsAnswered}</div>
          <div className="stat-label">Questions Answered</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{skippedQuestions.length}</div>
          <div className="stat-label">Questions Skipped</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{totalQuestionsAnswered + skippedQuestions.length}</div>
          <div className="stat-label">Total Questions</div>
        </div>
      </div>

      <div className="skipped-sections">
        {Object.entries(bySection).map(([section, questions]) => (
          <div key={section} className="section-group">
            <div className="section-header">
              <h3 className="section-title">{sectionNames[section] || section}</h3>
              <span className="section-count">{questions.length} question{questions.length > 1 ? 's' : ''}</span>
            </div>
            <div className="section-items">
              {questions.map((skip) => (
                <div key={skip.id} className="skip-item">
                  <div className="skip-question">{skip.question}</div>
                  <div className="skip-reason">{skip.reason}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="skipped-actions">
        <button className="continue-button" onClick={onContinue}>
          Continue to Results →
        </button>
        <p className="action-note">
          Your score is based on the {totalQuestionsAnswered} questions you answered.
        </p>
      </div>
    </div>
  );
};
